/**
 * AddContentSidebar.tsx — the schedule editor's content picker.
 *
 * Replaces the modal `AssetPickerModal` on the schedule editor: a docked
 * right-hand sidebar instead of a popup, so the operator can keep the
 * timeline in view and add several elements in a row — each with its own
 * "Añadir" button — without the dialog closing after every pick.
 *
 * The filters are the same ones the dialog carried (title search + tag
 * facet chips, AND-combined server-side via
 * `api.assetQueries.getReadyAssets`), so both surfaces speak one
 * vocabulary. Honest states preserved: `undefined` → skeleton, `[]` →
 * empty state; nothing is fabricated client-side.
 */
import { useState } from 'react';
import { useQuery } from 'convex/react';
import {
  EmptyState,
  Icon,
  Input,
  SkeletonList,
} from '../../components/primitives';
import { TAG_KIND_LABEL } from '../../components/tags/TagAutocompleteInput';
import type { TagKind } from '../../components/tags/TagAutocompleteInput';
import { fmtDur } from '../../lib/format';
import { api } from '@convex/api';

export interface AddContentSidebarProps {
  open: boolean;
  onClose: () => void;
  /** Append this asset to the schedule. May be called repeatedly. */
  onAdd: (assetId: string) => void;
  /** Asset id currently being appended (per-row loading state). */
  addingAssetId?: string | null;
  /** Any schedule mutation in flight — disables every add button. */
  busy?: boolean;
  /** Set when the schedule can't accept items yet (no channel assigned). */
  disabledReason?: string;
}

export function AddContentSidebar({
  open,
  onClose,
  onAdd,
  addingAssetId,
  busy,
  disabledReason,
}: AddContentSidebarProps) {
  const [q, setQ] = useState('');
  // Selected tag facet keys, `${kind}::${normalizedTag}` — the same identity
  // the Library filter bar and the old dialog used.
  const [selKeys, setSelKeys] = useState<Set<string>>(new Set());

  // Facet chips read the RESILIENT vocabulary (registry ∪ live in-use tags).
  // `minUsage: 1` keeps zero-usage catalog entries out. Subscribed only while
  // the sidebar is open.
  const facetData = useQuery(
    api.assetQueries.listTagVocabulary,
    open ? { minUsage: 1, limit: 200 } : 'skip',
  ) as
    | {
        tags: Array<{
          tag: string;
          kind: string;
          normalizedTag: string;
          usageCount: number;
        }>;
        count: number;
      }
    | undefined;
  const facets = facetData?.tags ?? [];
  const fkey = (f: { kind: string; normalizedTag: string }) =>
    `${f.kind}::${f.normalizedTag}`;
  const selectedFacets = facets.filter((f) => selKeys.has(fkey(f)));
  // Each facet carries its OWN kind so "Canal: deportes" never matches
  // category:deportes.
  const tagFacets = selectedFacets.map((f) => ({
    tag: f.normalizedTag,
    kind: f.kind,
  }));
  const tagFilterActive = tagFacets.length > 0;

  // Text and tags COMPOSE server-side: the title search index drives the read,
  // the tag facets intersect it (`tagMatchMode: "all"`). Skipped entirely while
  // the sidebar is closed so a hidden panel holds no subscription.
  const ready = useQuery(
    api.assetQueries.getReadyAssets,
    open
      ? {
          ...(q.trim() ? { search: q.trim() } : {}),
          ...(tagFacets.length > 0
            ? { tagFacets, tagMatchMode: 'all' as const }
            : {}),
        }
      : 'skip',
  ) as
    | {
        assets: Array<{
          _id: string;
          title?: string;
          durationMs?: number;
          assetType?: string;
        }>;
        count: number;
      }
    | undefined;
  const list = ready?.assets ?? [];

  if (!open) return null;

  return (
    <aside
      aria-label="Añadir contenido"
      style={{
        width: 264,
        flexShrink: 0,
        alignSelf: 'flex-start',
        position: 'sticky',
        top: 16,
        maxHeight: 'calc(100vh - 32px)',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        border: '1px solid var(--lightest-gray)',
        borderRadius: 16,
        background: 'var(--snow)',
        padding: 12,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <strong style={{ fontSize: 13.5 }}>Añadir contenido</strong>
        <button
          type="button"
          aria-label="Cerrar panel de contenido"
          onClick={onClose}
          style={{
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            color: 'var(--muted-slate)',
            display: 'flex',
          }}
        >
          <Icon name="x" size={16} />
        </button>
      </div>

      {disabledReason && (
        <p
          style={{
            margin: 0,
            fontSize: 12,
            color: 'var(--muted-slate)',
            lineHeight: 1.4,
          }}
        >
          {disabledReason}
        </p>
      )}

      <Input
        value={q}
        onChange={setQ}
        icon="search"
        placeholder="Buscar por título"
      />

      {/* Tag facet chips — independent toggles, AND-combined by the server.
          "Todas" clears the set. Hidden entirely when no tag is in use. */}
      {facets.length > 0 && (
        <div
          style={{
            display: 'flex',
            gap: 6,
            flexWrap: 'wrap',
            alignItems: 'center',
            maxHeight: 132,
            overflowY: 'auto',
          }}
        >
          <span
            className="t-mono"
            style={{ fontSize: 11, color: 'var(--muted-slate)', marginRight: 2 }}
          >
            Etiquetas
          </span>
          <button
            type="button"
            onClick={() => setSelKeys(new Set())}
            style={{
              border: '1px solid var(--border-cool)',
              cursor: 'pointer',
              borderRadius: 999,
              padding: '4px 10px',
              fontSize: 12,
              fontFamily: 'var(--font-sans)',
              background: !tagFilterActive ? 'var(--ink-muted)' : 'transparent',
              color: !tagFilterActive ? '#fff' : 'var(--slate)',
              borderColor: !tagFilterActive
                ? 'var(--ink-muted)'
                : 'var(--border-cool)',
            }}
          >
            Todas
          </button>
          {facets.map((f) => {
            const k = fkey(f);
            const active = selKeys.has(k);
            return (
              <button
                key={k}
                type="button"
                onClick={() =>
                  setSelKeys((prev) => {
                    const n = new Set(prev);
                    if (n.has(k)) n.delete(k);
                    else n.add(k);
                    return n;
                  })
                }
                style={{
                  border: '1px solid var(--border-cool)',
                  cursor: 'pointer',
                  borderRadius: 999,
                  padding: '4px 10px',
                  fontSize: 12,
                  fontFamily: 'var(--font-sans)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  background: active ? 'var(--ink-muted)' : 'transparent',
                  color: active ? '#fff' : 'var(--slate)',
                  borderColor: active ? 'var(--ink-muted)' : 'var(--border-cool)',
                }}
              >
                {f.kind !== 'freeform' && (
                  <span style={{ fontSize: 10, opacity: 0.65 }}>
                    {TAG_KIND_LABEL[f.kind as TagKind] ?? f.kind}:
                  </span>
                )}
                {f.tag}
                <span style={{ fontSize: 10, opacity: 0.6 }}>
                  {f.usageCount}
                </span>
              </button>
            );
          })}
          {tagFilterActive && (
            <span
              className="t-mono"
              style={{ fontSize: 10, color: 'var(--muted-slate)' }}
            >
              {tagFacets.length} activa(s) · deben coincidir todas
            </span>
          )}
        </div>
      )}

      <div
        style={{
          flex: 1,
          minHeight: 0,
          maxHeight: '50vh',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {ready === undefined ? (
          <SkeletonList rows={4} />
        ) : list.length === 0 ? (
          <EmptyState
            icon="film"
            title="Sin contenido listo"
            body={
              q.trim() || tagFilterActive
                ? 'Ningún video listo coincide con los filtros aplicados.'
                : 'No hay videos listos para elegir.'
            }
          />
        ) : (
          // No poster art exists for these assets yet, so the row carries no
          // thumbnail placeholder — just title, duration and the add button,
          // which keeps many more rows on screen at once.
          list.map((a) => {
            const adding = addingAssetId === a._id;
            return (
              <div
                key={a._id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  borderBottom: '1px solid var(--lightest-gray)',
                  padding: '6px 2px',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    title={a.title ?? '—'}
                    style={{
                      fontSize: 12.5,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {a.title ?? '—'}
                  </div>
                </div>
                <span
                  className="t-mono"
                  style={{
                    fontSize: 10,
                    color: 'var(--muted-slate)',
                    flexShrink: 0,
                  }}
                >
                  {a.durationMs !== undefined ? fmtDur(a.durationMs) : '—'}
                </span>
                <button
                  type="button"
                  aria-label={`Añadir "${a.title ?? 'sin título'}" a la programación`}
                  title={disabledReason ?? 'Añadir a la programación'}
                  disabled={busy || !!disabledReason || adding}
                  onClick={() => onAdd(a._id)}
                  style={{
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 24,
                    height: 24,
                    borderRadius: 7,
                    border: '1px solid var(--border-cool)',
                    background: 'transparent',
                    color: 'var(--slate)',
                    cursor:
                      busy || disabledReason || adding ? 'not-allowed' : 'pointer',
                    opacity: busy || disabledReason ? 0.5 : 1,
                  }}
                >
                  <Icon name={adding ? 'clock' : 'plus'} size={13} />
                </button>
              </div>
            );
          })
        )}
      </div>
    </aside>
  );
}

export default AddContentSidebar;
